import { Cartesian3, Cartographic } from "cesium";

import { TLocation } from "../components/paginas/Student/FlightLogs/FlightLogs";

interface KalmanFilterConfig {
	R?: number; // Process noise covariance
	Q?: number; // Measurement noise covariance
	A?: number; // State transition matrix
	B?: number; // Control matrix
	C?: number; // Measurement matrix
}

class KalmanFilter {
	private R: number; // Process noise covariance
	private Q: number; // Measurement noise covariance
	private A: number; // State transition matrix
	private B: number; // Control matrix
	private C: number; // Measurement matrix
	private covariance: number;
	private x: number;

	constructor({ R = 1, Q = 1, A = 1, B = 0, C = 1 }: KalmanFilterConfig = {}) {
		this.R = R;
		this.Q = Q;
		this.A = A;
		this.B = B;
		this.C = C;

		this.covariance = NaN;
		this.x = NaN;
	}

	public filter(z: number, u: number = 0): number {
		if (isNaN(this.x)) {
			this.x = (1 / this.C) * z;
			this.covariance = (1 / this.C) * this.Q * (1 / this.C);
		} else {
			// Prediction update
			const predictedX = this.A * this.x + this.B * u;
			const predictedCovariance = this.A * this.covariance * this.A + this.R;

			// Measurement update
			const K =
				predictedCovariance *
				this.C *
				(1 / (this.C * predictedCovariance * this.C + this.Q));
			this.x = predictedX + K * (z - this.C * predictedX);
			this.covariance = predictedCovariance - K * this.C * predictedCovariance;
		}

		return this.x;
	}
}

export function normalizeGpsData(gpsData: TLocation[]) {
	let betterPoints: TLocation[] = [];

	for (let i = 0; i < gpsData.length; i++) {
		const currentPoint = gpsData[i];
		const lastPoint = betterPoints[betterPoints.length - 1];
		if (
			lastPoint &&
			lastPoint.latitude === currentPoint.latitude &&
			lastPoint.longitude === currentPoint.longitude &&
			lastPoint.altitude === currentPoint.altitude
		) {
		} else if (currentPoint.speed < 0.1) {
		} else {
			betterPoints.push(currentPoint);
		}
	}

	const altitudesKalman = new KalmanFilter({ R: 0.01, Q: 0.1 });
	const mslAltitudesKalman = new KalmanFilter({ R: 0.01, Q: 0.1 });
	const latitudesKalman = new KalmanFilter({ R: 0.01, Q: 0.1 });
	const longitudesKalman = new KalmanFilter({ R: 0.01, Q: 0.1 });
	const speedKalman = new KalmanFilter({ R: 0.01, Q: 0.1 });

	betterPoints = betterPoints.map((point) => ({
		...point,
		altitude: altitudesKalman.filter(point.altitude),
		mslAltitude: mslAltitudesKalman.filter(point.mslAltitude ?? point.altitude),
		latitude: latitudesKalman.filter(point.latitude),
		longitude: longitudesKalman.filter(point.longitude),
		speed: speedKalman.filter(point.speed),
	}));
	betterPoints = betterPoints.map((point, index) => {
		let heading: number | undefined = undefined;
		let pitch: number | undefined = undefined;
		let roll: number | undefined = undefined;
		if (index < betterPoints.length - 1) {
			heading = calculateHeading(point, betterPoints[index + 1]);
			pitch = calculatePitch(point, betterPoints[index + 1]);
		}
		if (index < betterPoints.length - 2) {
			const { radius, crossProduct } = calculateTurnRadius(
				point,
				betterPoints[index + 1],
				betterPoints[index + 2],
			);
			roll = calculateBankAngle(point, radius, crossProduct.z);
		}

		return {
			...point,
			heading,
			pitch,
			roll,
		};
	});

	return betterPoints;
}

function calculateHeading(point1: TLocation, point2: TLocation) {
	const cartographic1 = Cartographic.fromCartesian(
		Cartesian3.fromDegrees(
			point1.longitude,
			point1.latitude,
			point1.mslAltitude ?? point1.altitude,
		),
	);
	const cartographic2 = Cartographic.fromCartesian(
		Cartesian3.fromDegrees(
			point2.longitude,
			point2.latitude,
			point2.mslAltitude ?? point2.altitude,
		),
	);

	// Extract longitude and latitude
	const lon1 = cartographic1.longitude;
	const lat1 = cartographic1.latitude;
	const lon2 = cartographic2.longitude;
	const lat2 = cartographic2.latitude;
	const heading = Math.atan2(
		Math.sin(lon2 - lon1) * Math.cos(lat2),
		Math.cos(lat1) * Math.sin(lat2) -
			Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1),
	);

	return heading - Math.PI / 2;
}

function calculatePitch(point1: TLocation, point2: TLocation) {
	const deltaAlt = point2.altitude - point1.altitude;
	const horizontalDistance = Cartesian3.distance(
		Cartesian3.fromDegrees(point1.longitude, point1.latitude, 0),
		Cartesian3.fromDegrees(point2.longitude, point2.latitude, 0),
	);

	const pitch = Math.atan2(deltaAlt, horizontalDistance);
	return pitch;
}

function calculateTurnRadius(
	point1: TLocation,
	point2: TLocation,
	point3: TLocation,
) {
	// Compute velocities
	const vectorAB = Cartesian3.subtract(
		Cartesian3.fromDegrees(
			point2.longitude,
			point2.latitude,
			point2.mslAltitude ?? point2.altitude,
		),
		Cartesian3.fromDegrees(
			point1.longitude,
			point1.latitude,
			point1.mslAltitude ?? point1.altitude,
		),
		new Cartesian3(),
	);
	const vectorAC = Cartesian3.subtract(
		Cartesian3.fromDegrees(
			point3.longitude,
			point3.latitude,
			point3.mslAltitude ?? point3.altitude,
		),
		Cartesian3.fromDegrees(
			point1.longitude,
			point1.latitude,
			point1.mslAltitude ?? point1.altitude,
		),
		new Cartesian3(),
	);

	// Compute the cross product of AB and AC to get a normal vector
	const crossProduct = Cartesian3.cross(vectorAB, vectorAC, new Cartesian3());
	const crossProductLength = Cartesian3.magnitude(crossProduct);

	// Compute the area of the triangle formed by A, B, and C using the cross product length
	const triangleArea = crossProductLength / 2;

	// Compute the lengths of the sides of the triangle
	const lengthAB = Cartesian3.magnitude(vectorAB);
	const lengthBC = Cartesian3.magnitude(
		Cartesian3.subtract(
			Cartesian3.fromDegrees(
				point3.longitude,
				point3.latitude,
				point3.mslAltitude ?? point3.altitude,
			),
			Cartesian3.fromDegrees(
				point2.longitude,
				point2.latitude,
				point2.mslAltitude ?? point2.altitude,
			),
			new Cartesian3(),
		),
	);
	const lengthCA = Cartesian3.magnitude(vectorAC);

	// Compute the circumradius of the triangle
	const radius = (lengthAB * lengthBC * lengthCA) / (4 * triangleArea);

	return { radius, crossProduct };
}

function calculateBankAngle(point: TLocation, radius: number, z: number) {
	// Constants
	const G = 9.81; // Gravitational acceleration in m/s²

	// Extract speed and bearing from GPS data
	const speed = point.speed; // Speed in m/s

	// Convert speed to velocity in m/s if it's not already
	const velocity = speed;

	// Calculate bank angle (in degrees)
	// Formula: bank = arctan(v²/rg)
	const bankAngle = Math.atan((velocity * velocity) / (radius * G));

	if (z > 0) {
		return -bankAngle;
	}

	return bankAngle;
}
